import { t } from '@hefe/i18n';
import {
  AnalysisIssuesOverviewSection,
  ResponseAnalysisGetIssuesMe,
} from '@hefe/models';
import { IssuesApi } from '@hefe/store';
import {
  Alert,
  Button,
  Flex,
  Image,
  List,
  Pagination,
  Segmented,
  Typography,
} from 'antd';
import { useEffect, useState } from 'react';
import smartThommyCheck from '../../../../../assets/images/smart_thommy/st_check.png';
import IssueListItemComponent from '../issue-list-item/issue-list-item.component';

const PER_PAGE = 50;
type IssueFilter = 'open' | 'hidden';

interface IssuesListProps {
  section: AnalysisIssuesOverviewSection;
}

export default function IssuesListComponent({ section }: IssuesListProps) {
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState<IssueFilter>('open');
  const queryArgs = {
    page,
    filter,
    per_page: PER_PAGE,
    only_open: false,
  };

  const personalQuery = IssuesApi.useFetchMyIssuesQuery(queryArgs, {
    skip: section.type !== 'personal',
  });
  const teamQuery = IssuesApi.useFetchTeamIssuesQuery(
    { ...queryArgs, teamId: section.type === 'team' ? section.team_id : 0 },
    { skip: section.type !== 'team' }
  );
  const clubQuery = IssuesApi.useFetchClubIssuesQuery(
    { ...queryArgs, clubId: section.type === 'club' ? section.club_id : 0 },
    { skip: section.type !== 'club' }
  );

  useEffect(() => {
    setPage(1);
  }, [section.key]);

  const query =
    section.type === 'personal'
      ? personalQuery
      : section.type === 'team'
      ? teamQuery
      : clubQuery;
  const data = query.currentData as ResponseAnalysisGetIssuesMe | undefined;

  const handleFilterChange = (nextFilter: IssueFilter) => {
    setFilter(nextFilter);
    setPage(1);
  };

  return (
    <Flex vertical gap={12}>
      <IssueFilterSelector filter={filter} onChange={handleFilterChange} />
      {query.isError ? (
        <Alert
          type="error"
          showIcon
          message={t('issues.court_vision.load_error')}
          action={
            <Button size="small" onClick={() => query.refetch()}>
              {t('issues.court_vision.retry')}
            </Button>
          }
        />
      ) : (
        <List
          loading={query.isLoading || query.isFetching}
          dataSource={data?.data ?? []}
          split={false}
          locale={{ emptyText: <EmptyIssues /> }}
          renderItem={(item) => (
            <IssueListItemComponent issue={item} key={item.id} />
          )}
        />
      )}
      {data && data.last_page > 1 && (
        <Pagination
          current={page}
          pageSize={PER_PAGE}
          total={data.total}
          onChange={setPage}
          showSizeChanger={false}
        />
      )}
    </Flex>
  );
}

function IssueFilterSelector({
  filter,
  onChange,
}: {
  filter: IssueFilter;
  onChange: (filter: IssueFilter) => void;
}) {
  return (
    <Segmented
      block
      value={filter}
      onChange={(value) => onChange(value as IssueFilter)}
      options={[
        { label: t('issues.court_vision.filters.open'), value: 'open' },
        { label: t('issues.court_vision.filters.hidden'), value: 'hidden' },
      ]}
    />
  );
}

function EmptyIssues() {
  return (
    <Flex vertical align="center" gap={8} style={{ padding: 8 }}>
      <Image
        style={{ width: 200, height: 200, objectFit: 'contain' }}
        src={smartThommyCheck}
        alt={t('issues.court_vision.no_issues')}
        preview={false}
      />
      <Typography.Text type="secondary">
        {t('issues.court_vision.no_issues')}
      </Typography.Text>
    </Flex>
  );
}
